import React from 'react';
import Modal from 'react-modal'
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getUserInfo } from '@/models/User';
import { updateCache } from '@/store/action';
import { showMessage, login } from '@/lib';
import { useStore } from '@/store';
import config from '@/config';
import { $api } from '@/queryClient';
import lang from '@/lib/lng';

const lng: { [key: string]: string } = lang[config?.lng];

Modal.setAppElement('#root');

// Validation schema với Yup
const validationSchema = yup.object({
  name: yup
    .string()
    .required('Họ và tên là bắt buộc')
    .min(2, 'Họ và tên phải có ít nhất 2 ký tự'),

  id_no: yup
    .string()
    .required('CMND/CCCD là bắt buộc')
    .matches(/^\d+$/, 'CMND/CCCD chỉ được nhập số')
    .min(9, 'CMND/CCCD phải có ít nhất 9 số')
    .max(12, 'CMND/CCCD không được quá 12 số'),

  address: yup
    .string()
    .required('Địa chỉ là bắt buộc')
    .min(5, 'Địa chỉ phải có ít nhất 5 ký tự'),

  phone_number: yup
    .string()
    .required('Số điện thoại là bắt buộc')
    .matches(/^\d+$/, 'Số điện thoại chỉ được nhập số')
    .matches(/^(0[1|3|5|7|8|9])+([0-9]{8})$/, 'Số điện thoại không đúng định dạng'),

  email_address: yup
    .string()
    .required('Email sinh viên là bắt buộc')
    .email('Email không đúng định dạng')
});

type FormData = {
  name: string;
  id_no: string;
  address: string;
  phone_number: string;
  email_address: string;
};

type Props = {
  modalStatus: boolean
  closeModal: (status: boolean) => void
}

const ModalRegister: React.FC<Props> = ({ modalStatus, closeModal }) => {
  // API Mutation Hook
  const { mutate: registerStudent } = $api.useMutation('post', '/api/user/student-register');

  // User info and store
  const userInfo = getUserInfo();
  const updateState = useStore((state) => state.updateState)
  const isCallingApi = useStore((state) => state.isCallingApi)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange'
  });

  const afterOpenModal = () => { };

  const selfClose = (event: React.SyntheticEvent) => {
    event.preventDefault();
    reset(); // Reset form khi đóng modal
    closeModal(false);
  };

  const onSubmit = async (formData: FormData) => {
    // Step 1: Event validation
    if (!userInfo?.payload?.event_started) {
      showMessage(config.eventMsg);
      return false;
    }

    // Step 2: User authentication
    if (!userInfo?.payload?.user?.id) {
      login();
      return false;
    }

    // Step 3: Prevent multiple calls
    if (isCallingApi) {
      return false;
    }

    // Step 5: Execute API call
    showMessage('Bạn có chắc chắn thông tin này là chính xác?', 'Xem lại').then((res) => {
      if (res.value) {
        updateState('isCallingApi', true);
        registerStudent(
          {
            body: {
              name: formData.name,
              address: formData.address,
              phone_number: formData.phone_number,
              email_address: formData.email_address,
              id_no: formData.id_no,
            }
          },
          {
            onSuccess: (resData) => {
              if (resData?.status === 'successful') {
                const oldCache = { ...userInfo };
                // showMessage('Đăng ký thông tin sinh viên thành công.').then(() => {
                updateCache(['get', '/api/user/get', {}], null, {
                  ...oldCache,
                  payload: {
                    ...oldCache?.payload,
                    user: {
                      ...oldCache?.payload?.user,
                      has_student_info: resData?.payload?.user?.has_student_info,
                      secret: resData?.payload?.user?.secret,
                      student_email: resData?.payload?.user?.student_email ?? formData.email_address,
                    },
                  },
                });
                updateState('isCallingApi', false);
                reset();
                closeModal(true);
                // });

              } else {
                const errCode: string = (resData as any)?.error_code ?? 'defaultError';

                showMessage(
                  lng[errCode] ? lng[errCode] : `${lng.defaultError} [${errCode}]`
                ).then(() => {
                  updateState('isCallingApi', false);
                });
              }
            },
            onError: () => {
              updateState('isCallingApi', false);
            },
          });
      }
    })
  };

  return (
    <>
      <Modal
        isOpen={modalStatus}
        onAfterOpen={afterOpenModal}
        onRequestClose={selfClose}
        contentLabel="Example Modal"
        portalClassName="ReactModalPortal"
        overlayClassName=""
        className="animated fadeInDown faster modal-register"
      >
        <div className="modal-description text-center welcome-modal-content register-wrapper">
          <h2 className="register-title">
            XÁC MINH TRẠNG THÁI SINH VIÊN CỦA BẠN<br />ĐỂ KÍCH HOẠT ĐẶC QUYỀN
          </h2>
          <div className="register welcome-text">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="register__form-item">
                <div className="form-row">
                  <label className="label">HỌ VÀ TÊN</label>
                  <div className="form-action">
                    <input
                      type="text"
                      placeholder="Nguyễn Văn A"
                      {...register('name')}
                      className={errors.name ? 'error' : ''}
                    />
                  </div>
                </div>
                {errors.name && (
                  <span className="error-message">{errors.name.message}</span>
                )}
              </div>

              <div className="register__form-item">
                <div className="form-row">
                  <label className="label">CMND/CCCD</label>
                  <div className="form-action">
                    <input
                      type="text"
                      placeholder="068xxxxxxxx"
                      {...register('id_no')}
                      className={errors.id_no ? 'error' : ''}
                    />
                  </div>
                </div>
                {errors.id_no && (
                  <span className="error-message">{errors.id_no.message}</span>
                )}
              </div>

              <div className="register__form-item">
                <div className="form-row">
                  <label className="label">SỐ ĐIỆN THOẠI</label>
                  <div className="form-action">
                    <input
                      type="text"
                      placeholder="038xxxxxxxx"
                      {...register('phone_number')}
                      className={errors.phone_number ? 'error' : ''}
                    />
                  </div>
                </div>
                {errors.phone_number && (
                  <span className="error-message">{errors.phone_number.message}</span>
                )}
              </div>

              <div className="register__form-item">
                <div className="form-row">
                  <label className="label">EMAIL SINH VIÊN</label>
                  <div className="form-action">
                    <input
                      type="email"
                      placeholder="abc@edu.vn"
                      {...register('email_address')}
                      className={errors.email_address ? 'error' : ''}
                    />
                  </div>
                </div>
                {errors.email_address && (
                  <span className="error-message email-error">
                    {errors.email_address.message}
                  </span>
                )}
              </div>

              <div className="register__form-item">
                <div className="form-row">
                  <label className="label">ĐỊA CHỈ</label>
                  <div className="form-action">
                    <input
                      type="text"
                      placeholder="xxxxx"
                      {...register('address')}
                      className={errors.address ? 'error' : ''}
                    />
                  </div>
                </div>
                {errors.address && (
                  <span className="error-message">{errors.address.message}</span>
                )}
              </div>

              <div className="register-note">
                *Lưu ý: Bạn vui lòng điền chính xác thông tin
              </div>

              <div className="register__form-item text-center">
                <button
                  type="submit"
                  className='btn'
                  disabled={isSubmitting || isCallingApi}
                >
                  {(isSubmitting || isCallingApi) ? 'Đang xử lý...' : 'XÁC NHẬN'}
                </button>
              </div>
            </form>
          </div>
        </div>
        <a
          href="#"
          className="close"
          onClick={(e) => {
            e.preventDefault();
            reset(); // Reset form khi đóng modal
            closeModal(false);
          }}
        ></a>
      </Modal>
    </>
  )
}

export default ModalRegister